import { supabase } from '../lib/supabase';

const BUCKET = 'region-associations';

const resolveStorageUrl = (url, path) => {
  if (url && typeof url === 'string' && url.trim()) return url.trim();
  if (path && typeof path === 'string' && path.trim()) {
    const {
      data: { publicUrl },
    } = supabase.storage.from(BUCKET).getPublicUrl(path.trim());
    return publicUrl || null;
  }
  return null;
};

const normalizeRegionalTeam = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    teamName: row.team_name,
    region: row.region || null,
    logoUrl: resolveStorageUrl(row.logo_url, row.logo_path),
    logoPath: row.logo_path || null,
    teamManager: row.team_manager,
    headCoach: row.head_coach,
    teamDoctor: row.team_doctor,
    teamStatistician: row.team_statistician,
    registrationStatus: row.registration_status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

export const regionalTeamService = {
  async uploadLogo(file, teamId) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    if (!teamId) throw new Error('Team ID is required for logo upload');
    if (!file) throw new Error('Logo file is required');

    let uploadFile = file;
    if (
      file.type?.startsWith('image/') &&
      !['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type)
    ) {
      try {
        const bitmap = await createImageBitmap(file);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(bitmap, 0, 0);
          if (typeof bitmap.close === 'function') bitmap.close();
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
          if (blob) {
            const baseName = (file.name || 'logo').replace(/\.[^.]+$/, '') || 'logo';
            uploadFile = new File([blob], `${baseName}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
          }
        }
      } catch (err) {
        console.warn('Logo conversion failed, uploading original:', err);
      }
    }

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(uploadFile.type) && !uploadFile.type?.startsWith('image/')) {
      throw new Error('Logo must be an image (JPEG, PNG, GIF, or WebP)');
    }
    if (uploadFile.size > 5 * 1024 * 1024) {
      throw new Error('Logo file size must be less than 5MB');
    }

    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 11);
    const sanitizedName = uploadFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const ext = sanitizedName.split('.').pop() || 'jpg';
    const nameWithoutExt = sanitizedName.substring(0, sanitizedName.lastIndexOf('.')) || sanitizedName;
    const filename = `${timestamp}-${randomStr}-${nameWithoutExt}.${ext}`;
    const filePath = `regional-teams/${teamId}/logo/${filename}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, uploadFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: uploadFile.type || undefined,
      });

    if (uploadError) {
      if (uploadError.message?.includes('Bucket not found')) {
        throw new Error(
          `Storage bucket "${BUCKET}" not found. Create it in Supabase Storage, then try again.`
        );
      }
      throw new Error(uploadError.message || 'Failed to upload logo');
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from(BUCKET).getPublicUrl(filePath);

    return { path: filePath, url: publicUrl, fileName: uploadFile.name, fileSize: uploadFile.size };
  },

  async create(formData) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const payload = {
      team_name: formData.teamName?.trim(),
      region: formData.region || null,
      team_manager: formData.teamManager?.trim() || null,
      head_coach: formData.headCoach?.trim() || null,
      team_doctor: formData.teamDoctor?.trim() || null,
      team_statistician: formData.teamStatistician?.trim() || null,
      registration_status: 'pending',
      created_by: user.id,
    };

    const { data, error } = await supabase
      .from('regional_teams')
      .insert(payload)
      .select()
      .single();

    if (error) {
      throw new Error(error.message || 'Failed to create regional team');
    }

    if (formData.logoFile) {
      try {
        const uploaded = await this.uploadLogo(formData.logoFile, data.id);
        const { data: updated, error: updateError } = await supabase
          .from('regional_teams')
          .update({
            logo_url: uploaded.url,
            logo_path: uploaded.path,
            updated_at: new Date().toISOString(),
          })
          .eq('id', data.id)
          .select()
          .single();

        if (updateError) {
          console.error('Failed to save logo URL:', updateError);
          return normalizeRegionalTeam(data);
        }
        return normalizeRegionalTeam(updated);
      } catch (uploadError) {
        console.error('Logo upload failed:', uploadError);
        return {
          ...normalizeRegionalTeam(data),
          uploadWarning: uploadError.message,
        };
      }
    }

    return normalizeRegionalTeam(data);
  },

  async getAll() {
    const { data, error } = await supabase
      .from('regional_teams')
      .select('*')
      .order('team_name', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load regional teams');
    return (data || []).map(normalizeRegionalTeam);
  },

  async getById(id) {
    const { data, error } = await supabase
      .from('regional_teams')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw new Error(error.message || 'Failed to load regional team');
    return normalizeRegionalTeam(data);
  },

  async getByRegion(region) {
    if (!region) return [];

    const { data, error } = await supabase
      .from('regional_teams')
      .select('*')
      .eq('region', region)
      .order('team_name', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load regional teams');
    return (data || []).map(normalizeRegionalTeam);
  },

  async update(id, formData) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    if (!id) throw new Error('Team ID is required');

    const payload = {
      team_name: formData.teamName?.trim(),
      region: formData.region || null,
      team_manager: formData.teamManager?.trim() || null,
      head_coach: formData.headCoach?.trim() || null,
      team_doctor: formData.teamDoctor?.trim() || null,
      team_statistician: formData.teamStatistician?.trim() || null,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('regional_teams')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to update regional team');

    if (formData.logoFile) {
      try {
        const uploaded = await this.uploadLogo(formData.logoFile, id);
        const { data: updated, error: updateError } = await supabase
          .from('regional_teams')
          .update({
            logo_url: uploaded.url,
            logo_path: uploaded.path,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id)
          .select()
          .single();

        if (updateError) {
          console.error('Failed to save logo URL:', updateError);
          return {
            ...normalizeRegionalTeam(data),
            uploadWarning: updateError.message,
          };
        }
        return normalizeRegionalTeam(updated);
      } catch (uploadError) {
        console.error('Logo upload failed:', uploadError);
        return {
          ...normalizeRegionalTeam(data),
          uploadWarning: uploadError.message,
        };
      }
    }

    return normalizeRegionalTeam(data);
  },

  async delete(id) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    if (!id) throw new Error('Team ID is required');

    const { error } = await supabase.from('regional_teams').delete().eq('id', id);
    if (error) throw new Error(error.message || 'Failed to delete regional team');
    return true;
  },
};
